import React, { useState } from 'react';
import { Brain, Target, Award, CheckCircle, Users, BarChart, ArrowRight, Book, Briefcase, Rocket, ArrowUpRight, Clock } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

type SkillGap = {
  id: string;
  department: string;
  skill: string;
  currentLevel: number;
  requiredLevel: number;
  employeesAffected: number;
  priority: 'alta' | 'media' | 'baja';
};

type LearningModule = {
  id: string;
  title: string;
  description: string;
  skills: string[];
  duration: string;
  employees: number;
  progress: number;
  status: 'active' | 'completed' | 'pending';
};

const AIWorkforceDevelopment = () => {
  const [activeTab, setActiveTab] = useState('analysis');

  // Mock data for skill gaps
  const skillGaps: SkillGap[] = [
    {
      id: '1',
      department: 'Tecnología',
      skill: 'Cloud Computing (AWS)',
      currentLevel: 2,
      requiredLevel: 4,
      employeesAffected: 25,
      priority: 'alta'
    },
    {
      id: '2',
      department: 'Marketing',
      skill: 'Análisis de Datos',
      currentLevel: 3,
      requiredLevel: 4,
      employeesAffected: 15,
      priority: 'media'
    },
    {
      id: '3',
      department: 'Ventas',
      skill: 'Negociación Avanzada',
      currentLevel: 2,
      requiredLevel: 3,
      employeesAffected: 30,
      priority: 'alta'
    }
  ];

  // Mock data for learning modules
  const learningModules: LearningModule[] = [
    {
      id: '1',
      title: 'AWS Cloud Fundamentals',
      description: 'Fundamentos de computación en la nube con AWS',
      skills: ['AWS', 'Cloud Computing', 'DevOps'],
      duration: '4 semanas',
      employees: 25,
      progress: 65,
      status: 'active'
    },
    {
      id: '2',
      title: 'Análisis de Datos para Marketing',
      description: 'Curso avanzado de análisis de datos para equipos de marketing',
      skills: ['Data Analysis', 'Marketing Analytics', 'Python'],
      duration: '6 semanas',
      employees: 15,
      progress: 30,
      status: 'active'
    }
  ];

  // Chart data
  const employabilityData = {
    labels: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Índice de Empleabilidad',
        data: [75, 78, 80, 82, 85, 88],
        borderColor: 'rgb(124, 58, 237)',
        backgroundColor: 'rgba(124, 58, 237, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };

  // Chart options
  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function(value) {
            return value + '%';
          }
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="border-b">
        <nav className="-mb-px flex" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('analysis')}
            className={`${
              activeTab === 'analysis'
                ? 'border-accent text-accent'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } w-1/3 py-4 px-1 text-center border-b-2 font-medium text-sm`}
          >
            Análisis de Habilidades
          </button>
          <button
            onClick={() => setActiveTab('training')}
            className={`${
              activeTab === 'training'
                ? 'border-accent text-accent'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } w-1/3 py-4 px-1 text-center border-b-2 font-medium text-sm`}
          >
            Programas de Formación
          </button>
          <button
            onClick={() => setActiveTab('metrics')}
            className={`${
              activeTab === 'metrics'
                ? 'border-accent text-accent'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } w-1/3 py-4 px-1 text-center border-b-2 font-medium text-sm`}
          >
            Métricas de Empleabilidad
          </button>
        </nav>
      </div>

      <div className="p-6">
        {activeTab === 'analysis' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">Análisis de Brechas de Habilidades</h3>
              <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90">
                <BarChart className="h-4 w-4 mr-2" />
                Generar Reporte
              </button>
            </div>

            <div className="space-y-6">
              {skillGaps.map((gap) => (
                <div
                  key={gap.id}
                  className="border rounded-lg p-6 hover:border-accent/50 hover:shadow-sm transition-all duration-300"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">{gap.skill}</h4>
                      <p className="text-gray-600">Departamento: {gap.department}</p>
                      
                      <div className="mt-4 flex items-center space-x-4">
                        <div className="flex items-center">
                          <Users className="h-4 w-4 text-gray-400 mr-1" />
                          <span className="text-sm text-gray-500">
                            {gap.employeesAffected} empleados afectados
                          </span>
                        </div>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            gap.priority === 'alta'
                              ? 'bg-red-100 text-red-800'
                              : gap.priority === 'media'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-green-100 text-green-800'
                          }`}
                        >
                          Prioridad {gap.priority}
                        </span>
                      </div>

                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-500">Nivel Actual vs. Requerido</span>
                          <span className="text-sm text-gray-500">
                            {gap.currentLevel} / {gap.requiredLevel}
                          </span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full">
                          <div
                            className="h-2 bg-accent rounded-full"
                            style={{ width: `${(gap.currentLevel / gap.requiredLevel) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90">
                      Ver Plan de Acción
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'training' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">Programas de Formación Activos</h3>
              <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90">
                <Book className="h-4 w-4 mr-2" />
                Nuevo Programa
              </button>
            </div>

            <div className="space-y-6">
              {learningModules.map((module) => (
                <div
                  key={module.id}
                  className="border rounded-lg p-6 hover:border-accent/50 hover:shadow-sm transition-all duration-300"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">{module.title}</h4>
                      <p className="text-gray-600">{module.description}</p>

                      <div className="mt-4 flex items-center space-x-4">
                        <div className="flex items-center">
                          <Users className="h-4 w-4 text-gray-400 mr-1" />
                          <span className="text-sm text-gray-500">
                            {module.employees} participantes
                          </span>
                        </div>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 text-gray-400 mr-1" />
                          <span className="text-sm text-gray-500">{module.duration}</span>
                        </div>
                      </div>

                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-500">Progreso</span>
                          <span className="text-sm text-gray-500">{module.progress}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full">
                          <div
                            className="h-2 bg-accent rounded-full"
                            style={{ width: `${module.progress}%` }}
                          ></div>
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2">
                        {module.skills.map((skill, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent/10 text-accent"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>

                    <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90">
                      Ver Detalles
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'metrics' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">Métricas de Empleabilidad</h3>
              <div className="flex space-x-4">
                <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-accent focus:border-accent">
                  <option>Últimos 6 meses</option>
                  <option>Último año</option>
                  <option>Últimos 2 años</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="dark-card p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Índice de Empleabilidad</p>
                    <h4 className="text-2xl font-bold text-gray-900">88%</h4>
                  </div>
                  <Brain className="h-8 w-8 text-accent" />
                </div>
                <div className="mt-2">
                  <span className="text-green-500 text-sm flex items-center">
                    <ArrowUpRight className="h-4 w-4 mr-1" />
                    +5% vs mes anterior
                  </span>
                </div>
              </div>

              <div className="dark-card p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Habilidades Mejoradas</p>
                    <h4 className="text-2xl font-bold text-gray-900">156</h4>
                  </div>
                  <Target className="h-8 w-8 text-accent" />
                </div>
                <div className="mt-2">
                  <span className="text-green-500 text-sm flex items-center">
                    <ArrowUpRight className="h-4 w-4 mr-1" />
                    +12 esta semana
                  </span>
                </div>
              </div>

              <div className="dark-card p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Certificaciones Obtenidas</p>
                    <h4 className="text-2xl font-bold text-gray-900">89</h4>
                  </div>
                  <Award className="h-8 w-8 text-accent" />
                </div>
                <div className="mt-2">
                  <span className="text-green-500 text-sm flex items-center">
                    <ArrowUpRight className="h-4 w-4 mr-1" />
                    +8 este mes
                  </span>
                </div>
              </div>
            </div>

            <div className="dark-card p-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Tendencia de Empleabilidad</h4>
              <div style={{ height: '300px', position: 'relative' }}>
                <Line data={employabilityData} options={chartOptions} />
              </div>
            </div>

            <div className="mt-8 bg-accent/5 p-6 rounded-lg">
              <div className="flex items-start">
                <Brain className="h-6 w-6 text-accent flex-shrink-0" />
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">Recomendaciones de la IA</h4>
                  <ul className="mt-4 space-y-4">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                      <p className="ml-3 text-gray-600">
                        Enfocarse en mejorar habilidades de Cloud Computing en el departamento de Tecnología
                      </p>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                      <p className="ml-3 text-gray-600">
                        Implementar programa de mentoring para acelerar el desarrollo de habilidades de liderazgo
                      </p>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                      <p className="ml-3 text-gray-600">
                        Expandir el programa de certificaciones técnicas para mejorar la empleabilidad
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIWorkforceDevelopment;